// Existing imports
import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { db } from './firebase'; // Firebase setup
import { doc, onSnapshot, getDocFromServer, getDoc, onSnapshotsInSync } from 'firebase/firestore';
import Hero from './components/Hero';
import About from './components/About';
import Menu from './components/Menu';
import Team from './components/Team';
import Footer from './components/Footer';
import Tables from './components/Tables';
import Tables2 from './components/Tables2.0';
import Header from './components/Header'; // Assuming  Header component includes  Nav
import Demonstrate from './components/Demonstrate';
import ThreeHighlights from './components/ThreeHighlights';
import PriceCalc from './components/PriceCalc';
import BuildMode from './components/BuildMode';
import Portal from './Portal';
import PortalPage from './components/PortalPage';
import AccountModal from './components/AccountModal';
import { reservationVariants } from './variants';
import LoadingLogo from './assets/img/app/gohost_meta_logo.png'
import HeroLogo from './assets/img/hero/gohost_header_1.png'
import HeroModel from './assets/img/model-black.png'
import { getAuth, onAuthStateChanged } from "firebase/auth";
import WaitlistPreorder from './components/WaitlistPreorder';

// Export the hardcoded restaurant ID
export const restaurantID = 'thaicoon'; 

const App = () => {
  // Create refs for each section you want to scroll to
  const [buildMode, setBuildMode] = useState(undefined);
  const [buildModeResolved, setBuildModeResolved] = useState(false);
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const demonstrateRef = useRef(null);
  const tablesRef = useRef(null);
  const threeHighlightsref = useRef(null);
  const menuRef = useRef(null);
  const teamRef = useRef(null);
  const footerRef = useRef(null);
  const headerRef = useRef(null);

  const headerHeight = -50; // Example: 100px

  const [heroImagesReady, setHeroImagesReady] = useState(false);
  const heroLoadCount = useRef(0);
  const onHeroAssetLoaded = () => {
    heroLoadCount.current += 1;
    if (heroLoadCount.current >= 2) setHeroImagesReady(true);
  };

  const [allowWebReservations, setAllowWebReservations] = useState(true);
  const [deepLinkedReservationId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reservation') || null;
  });
  


  const scrollToRef = (ref) => {
    if (ref.current) {
      const position = ref.current.offsetTop - headerHeight;
      window.scrollTo({
        top: position,
        behavior: 'smooth',
      });
    }
  };

  const scrollToMenu = () => {
    scrollToRef(menuRef);
  };
  
  useEffect(() => {
    if (!buildModeResolved) {
      const id = setInterval(() => {
        console.log("Waiting for buildMode from Firestore...");
      }, 5000);
      return () => clearInterval(id);
    }
  }, [buildModeResolved]);

  useEffect(() => {
    const checkRestaurant = async () => {
      try {
        const docRef = doc(db, 'restaurants_data', restaurantID);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setAllowWebReservations(data.allowWebReservations !== false);
        } else {
          console.warn(`Restaurant '${restaurantID}' not found in Firestore.`);
        }
      } catch (error) {
        console.error('Error checking restaurant:', error);
      }
    };

    checkRestaurant();
  }, []);

// UNCOMMENT BELOW BEFORE DEPLOYING AND SWITCH BUILD MODE BOOLEAN BEFORE DEPLOYING

useEffect(() => {
  let active = true;
  let unsub = null;
  let intervalId = null;

  const tryResolveBuildMode = async () => {
    if (!active) return;
    if (buildModeResolved) return;

    try {
      const ref = doc(db, "restaurants_data", restaurantID);

      // 1) Try a direct server fetch first (avoids cache/persistence weirdness)
      const snap = await getDocFromServer(ref);

      if (!active) return;

      if (snap.exists()) {
        const data = snap.data();

        if (typeof data.buildMode === "boolean") {
          setBuildMode(data.buildMode);
          setBuildModeResolved(true);

          // stop any future work
          if (intervalId) clearInterval(intervalId);
          if (unsub) unsub();
          return;
        }
      }

      // If doc exists but buildMode missing/invalid, keep waiting (your requirement)
      console.warn("buildMode not yet valid; still waiting...");

    } catch (err) {
      // Do NOT resolve. Keep waiting. But log so you can see whether it’s auth/rules/etc.
      console.error("Still waiting for buildMode (server fetch failed):", err);
    }
  };

  const attachSnapshot = () => {
    if (!active) return;
    if (unsub) return;

    const ref = doc(db, "restaurants_data", restaurantID);

    // 2) Also attach a realtime listener; if the SDK wakes up, this can resolve instantly
    unsub = onSnapshot(
      ref,
      (snap) => {
        if (!active) return;
        if (buildModeResolved) return;

        if (snap.exists()) {
          const data = snap.data();
          if (typeof data.buildMode === "boolean") {
            setBuildMode(data.buildMode);
            setBuildModeResolved(true);

            if (intervalId) clearInterval(intervalId);
            if (unsub) unsub();
          }
        }
      },
      (err) => {
        // Listener errors shouldn't resolve; just keep retrying
        console.error("onSnapshot buildMode listener error:", err);
      }
    );
  };

  // Start immediately
  attachSnapshot();
  tryResolveBuildMode();

  // Retry every 500ms until resolved
  intervalId = setInterval(() => {
    attachSnapshot();
    tryResolveBuildMode();
  }, 500);

  return () => {
    active = false;
    if (intervalId) clearInterval(intervalId);
    if (unsub) unsub();
  };
}, [buildModeResolved]);




    // Bypass buildMode gate for paths that are restaurant-agnostic public pages
    const isPublicPath = window.location.pathname.startsWith('/waitlist-preorder');

    if ((!buildModeResolved || !heroImagesReady) && !isPublicPath) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-neutral-700">
          {/* Silently preload hero images so they're cached before the screen dismisses */}
          <img src={HeroLogo}  alt="" style={{ display: 'none' }} onLoad={onHeroAssetLoaded} onError={onHeroAssetLoaded} />
          <img src={HeroModel} alt="" style={{ display: 'none' }} onLoad={onHeroAssetLoaded} onError={onHeroAssetLoaded} />
          <img
            src={LoadingLogo}
            alt="GoHost loading"
            className="w-16 h-auto opacity-90 animate-pulse"
          />
        </div>
      );
    }

      // If buildMode is true, short-circuit render (but still allow public paths through)
      if (buildMode && !isPublicPath) {
        return <BuildMode />;
      }


  return (
    <Routes>
      <Route path="/waitlist-preorder" element={<WaitlistPreorder />} />
      <Route path="/portal" element={<PortalPage />} />
      <Route path="/*" element={<BusinessSite
        headerRef={headerRef}
        heroRef={heroRef}
        aboutRef={aboutRef}
        demonstrateRef={demonstrateRef}
        tablesRef={tablesRef}
        threeHighlightsref={threeHighlightsref}
        menuRef={menuRef}
        teamRef={teamRef}
        footerRef={footerRef}
        scrollToRef={scrollToRef}
        scrollToMenu={scrollToMenu}
        allowWebReservations={allowWebReservations}
        deepLinkedReservationId={deepLinkedReservationId}
      />} />
    </Routes>
  );
};

const BusinessSite = ({
  headerRef, heroRef, aboutRef, demonstrateRef, tablesRef, threeHighlightsref,
  menuRef, teamRef, footerRef, scrollToRef, scrollToMenu,
  allowWebReservations, deepLinkedReservationId,
}) => {
  const [isAccountModalOpen, setIsAccountModalOpen] = React.useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('login') === '1';
  });

  return (
    <div className='h-full bg-pattern bg-repeat mx-auto overflow-hidden'>
      <Header
        ref={headerRef}
        scrollToRef={scrollToRef}
        deepLinkReservationId={deepLinkedReservationId}
        onMyAccountClick={() => setIsAccountModalOpen(true)}
        refs={{
          heroRef,
          aboutRef,
          demonstrateRef,
          tablesRef,
          threeHighlightsref,
          menuRef,
          teamRef,
          footerRef,
        }}
        className="relative z-40"
      />
      <AccountModal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
      />

      <div ref={heroRef}>
        <Hero scrollToMenu={scrollToMenu} />
      </div>
      <div ref={aboutRef} className="relative z-30">
        <About scrollToMenu={scrollToMenu} />
      </div>

      <div ref={demonstrateRef} className="relative z-20">
        <Demonstrate />
      </div>


{allowWebReservations && (
  <div ref={tablesRef}>
    {/* <Tables headerRef={headerRef} /> */}
    <Tables2 headerRef={headerRef} />
  </div>
)}
      <div>
        <ThreeHighlights />
      </div>
      <div style={{ backgroundColor: 'rgba(253,253,251,1)' }}>
        <PriceCalc />
      </div>
      <div ref={menuRef}>
         <Menu restaurantID={restaurantID} scrollToFooter={() => {
  if (footerRef.current) {
    window.scrollTo({ top: footerRef.current.offsetTop + 200, behavior: 'smooth' });
  }
}} />

      </div>
      <div ref={teamRef}>
        <Team />
      </div>
      <div ref={footerRef}>
        <Footer />
      </div>
      <div className='h-[380px] md:h-[370px]'></div>
    </div>
  );
};

export default App;
